import React, { useState, useEffect } from "react";
import { isSacredAdminMode, getEditorAuth } from "@/lib/sacred-library/sacred-library-api";
import { canAccessAdminRoutes } from "@/lib/auth/roles";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "../i18n/I18nContext";
import {
  Menu, X, LogOut, LifeBuoy, ChevronDown,
} from "lucide-react";

const PRIMARY_LINKS = [
  { to: "/knowledge", key: "nav.knowledge", testId: "nav-knowledge" },
  { to: "/library", key: "nav.library", testId: "nav-library" },
  { to: "/panchanga", key: "nav.panchanga", testId: "nav-panchanga" },
  { to: "/prayer-hall", key: "nav.prayer_hall", testId: "nav-prayer-hall" },
  { to: "/sadhana", key: "nav.sadhana", testId: "nav-sadhana" },
  { to: "/satsanga", key: "nav.satsanga", testId: "nav-satsanga" },
  { to: "/guide", key: "nav.acharya", testId: "nav-guide" },
];

const KNOWLEDGE_SECONDARY = [
  { to: "/knowledge?section=gates", key: "nav.gates", label: "12 Gates" },
  { to: "/knowledge?section=adhikara", key: "nav.adhikara", label: "4 Adhikāra" },
  { to: "/knowledge?section=pathways", key: "nav.pathways", label: "4 Pathways" },
  { to: "/knowledge?section=curriculum", key: "nav.curriculum", label: "Curriculum" },
  { to: "/dharma-decisions", key: "nav.dharma_decisions", label: "Dharma Decisions" },
  { to: "/crisis", key: "nav.crisis", label: "Crisis Guidance" },
];

export function AppNavigation({ user, logout, logo: Logo, onCrisisHelp }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [sacredAdminMode, setSacredAdminMode] = useState(false);

  const [sacredEditor, setSacredEditor] = useState(() => getEditorAuth().user);

  useEffect(() => {
    const sync = () => {
      setSacredAdminMode(isSacredAdminMode());
      setSacredEditor(getEditorAuth().user);
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);
  const navigate = useNavigate();
  const { t, lang, setLang, supportedLangs } = useI18n();

  const closeMobile = () => setMenuOpen(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            {Logo && <Logo />}
            <span className="font-heading text-lg sm:text-xl font-semibold text-primary">VedaPath</span>
          </Link>

          <div className="hidden lg:flex items-center gap-1 xl:gap-2 flex-1 justify-center max-w-4xl">
            <div className="relative group">
              <Link
                to="/knowledge"
                className="inline-flex items-center gap-0.5 px-2 py-1.5 text-sm text-foreground/80 hover:text-primary rounded-md hover:bg-accent/30"
                data-testid="nav-knowledge"
              >
                {t("nav.knowledge")}
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </Link>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-white/95 backdrop-blur border border-border/60 rounded-lg shadow-lg py-2 min-w-[200px]">
                  {KNOWLEDGE_SECONDARY.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="block px-4 py-2 text-sm text-foreground/80 hover:bg-accent/40 hover:text-primary"
                    >
                      {t(item.key) || item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {PRIMARY_LINKS.filter((l) => l.to !== "/knowledge").map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-2 py-1.5 text-sm text-foreground/80 hover:text-primary whitespace-nowrap rounded-md hover:bg-accent/30"
                data-testid={link.testId}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-900 h-8 text-xs"
              onClick={onCrisisHelp}
              data-testid="nav-crisis-help"
            >
              <LifeBuoy className="w-3.5 h-3.5 mr-1" />
              {t("nav.help_now") || "Help"}
            </Button>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="text-xs border border-border/80 rounded-md px-1.5 py-1 bg-white/70 max-w-[5rem]"
              aria-label={t("nav.language")}
            >
              {supportedLangs.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            {user ? (
              <>
                <Link to="/dashboard" className="text-xs text-foreground/80 hover:text-primary px-1" data-testid="nav-dashboard">
                  {t("nav.dashboard")}
                </Link>
                {user && (
                  <Link to="/journal" className="text-xs text-foreground/80 hover:text-primary px-1">
                    {t("nav.journal") || "Journal"}
                  </Link>
                )}
                {(canAccessAdminRoutes(user.role) || user.role === "teacher") && (
                  <Link to="/admin" className="text-xs text-foreground/80 hover:text-primary px-1">Admin</Link>
                )}
                {(user.role === "teacher") && (
                  <Link to="/teacher" className="text-xs text-foreground/80 hover:text-primary px-1" data-testid="nav-teacher">
                    Teacher
                  </Link>
                )}
                {sacredEditor ? (
                  <Link to="/library" className="text-xs text-primary font-medium px-1" data-testid="nav-sacred-staff">
                    Library staff
                  </Link>
                ) : (
                  <Link to="/sacred-admin.html" className="text-xs text-foreground/80 hover:text-primary px-1" data-testid="nav-sacred-login">
                    Staff login
                  </Link>
                )}
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => { void logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button size="sm" className="h-8" onClick={() => navigate("/login")}>{t("nav.sign_in")}</Button>
            )}
          </div>

          <button type="button" className="lg:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden py-3 border-t border-border/50 space-y-1 max-h-[70vh] overflow-y-auto">
            <button
              type="button"
              className="w-full flex items-center justify-between py-2 px-1 text-foreground/80"
              onClick={() => setKnowledgeOpen(!knowledgeOpen)}
            >
              <span>{t("nav.knowledge")}</span>
              <ChevronDown className={`w-4 h-4 transition ${knowledgeOpen ? "rotate-180" : ""}`} />
            </button>
            {knowledgeOpen && (
              <div className="pl-4 space-y-1 border-l-2 border-primary/20 ml-2 mb-2">
                <Link to="/knowledge" className="block py-1.5 text-sm" onClick={closeMobile}>{t("nav.knowledge_overview") || "Overview"}</Link>
                {KNOWLEDGE_SECONDARY.map((item) => (
                  <Link key={item.to} to={item.to} className="block py-1.5 text-sm text-muted-foreground" onClick={closeMobile}>
                    {t(item.key) || item.label}
                  </Link>
                ))}
              </div>
            )}
            {PRIMARY_LINKS.filter((l) => l.to !== "/knowledge").map((link) => (
              <Link key={link.to} to={link.to} className="block py-2 text-foreground/80" onClick={closeMobile}>
                {t(link.key)}
              </Link>
            ))}
            <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => { onCrisisHelp(); closeMobile(); }}>
              <LifeBuoy className="w-4 h-4 mr-2" /> {t("nav.help_now") || "Help now"}
            </Button>
            {user ? (
              <>
                <Link to="/dashboard" className="block py-2" onClick={closeMobile}>{t("nav.dashboard")}</Link>
                <Link to="/journal" className="block py-2" onClick={closeMobile}>{t("nav.journal") || "Journal"}</Link>
                <button type="button" className="block py-2 w-full text-left" onClick={() => { void logout(); navigate("/"); closeMobile(); }}>{t("nav.logout")}</button>
              </>
            ) : (
              <Link to="/login" className="block py-2 text-primary font-medium" onClick={closeMobile}>{t("nav.sign_in")}</Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
